import * as React from 'react'
import * as Popover from '@radix-ui/react-popover'
import { Link, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Bell, CheckCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { notificationSummaryQuery, useNotificationActions, type Notification } from '@/api/me'
import { fmtRelative } from '@/lib/format'
import { cn } from '@/lib/utils'
import { SeverityDot } from './components/notification-bits'

/**
 * Top-bar notifications: unread count (polled every 30 s and on focus) and a
 * popover with the latest eight. Opening one marks it read and follows its link.
 */
export function NotificationsBell() {
  const q = useQuery(notificationSummaryQuery())
  const { read, readAll } = useNotificationActions()
  const navigate = useNavigate()
  const [open, setOpen] = React.useState(false)
  const unread = q.data?.unread ?? 0
  const label = unread ? `Notifications, ${unread} unread` : 'Notifications'

  const openOne = (n: Notification) => {
    setOpen(false)
    if (!n.read) read.mutate({ id: n.id, read: true })
    void navigate({ to: n.link ?? '/me/notifications' })
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button variant="ghost" size="icon" aria-label={label} className="relative" data-testid="notifications-bell">
          <Bell />
          {unread > 0 && (
            <span data-testid="notifications-badge" className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-red px-1 text-[9.5px] font-bold leading-none text-white tabular">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={6} className="z-[60] w-[min(380px,calc(100vw-24px))] rounded-xl border border-line bg-surface shadow-2xl">
          <div className="flex items-center justify-between gap-2 border-b border-line px-3.5 py-2.5">
            <div className="text-[13px] font-semibold text-ink">Notifications</div>
            <Button variant="ghost" size="sm" disabled={!unread || readAll.isPending} onClick={() => readAll.mutate(undefined)}>
              <CheckCheck />
              Mark all read
            </Button>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {q.isPending ? (
              <p className="px-4 py-6 text-center text-[12px] text-muted" role="status">Loading…</p>
            ) : q.error ? (
              <p className="px-4 py-6 text-center text-[12px] text-muted">Notifications can’t be loaded right now.</p>
            ) : !q.data?.latest.length ? (
              <p className="px-4 py-8 text-center text-[12px] text-muted">You’re all caught up. New notices will appear here.</p>
            ) : (
              <ul>
                {q.data.latest.map(n => (
                  <li key={n.id}>
                    <button type="button" onClick={() => openOne(n)} className={cn('flex w-full items-start gap-2.5 border-b border-line px-3.5 py-2.5 text-left last:border-b-0 hover:bg-soft', !n.read && 'bg-accent-soft/40')}>
                      <SeverityDot severity={n.severity} unread={!n.read} />
                      <span className="min-w-0 flex-1">
                        <span className={cn('block text-[12.5px] leading-snug text-ink', !n.read && 'font-semibold')}>{n.title}</span>
                        <span className="mt-0.5 line-clamp-2 block text-[11.5px] leading-snug text-muted">{n.body}</span>
                        <span className="mt-1 block text-[10.5px] text-muted">{fmtRelative(n.created_at)}{n.org ? ` · ${n.org.name}` : ''}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border-t border-line px-3.5 py-2">
            <Link to="/me/notifications" onClick={() => setOpen(false)} className="text-[12px] font-semibold text-accent hover:underline">
              See all notifications
            </Link>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
